// API service for ThermalX frontend, configured for BFF authentication (session cookies)
import  { AxiosError, AxiosResponse, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { createAxiosInstance } from './apiConfig';

// Creating API client
const api: AxiosInstance = createAxiosInstance();

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add CSRF token if available (for Django)
    const csrfTokenCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrftoken='));
    
    const csrfToken = csrfTokenCookie ? csrfTokenCookie.split('=')[1] : null;
      
    // Ensure headers object exists
    config.headers = config.headers ?? {}; 

    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
    
    // Add a custom header to identify requests from this frontend
    config.headers['X-ThermalX-Frontend'] = 'true';
    return config;
  },
  (error: AxiosError) => { 
    return Promise.reject(new Error(error.message));
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError<any>) => { 
    // Handle 401 (Unauthorized) errors
    if (error.response && error.response.status === 401) {
      console.error('API Error: Unauthorized access (401). Session may be invalid or expired.');
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    
    // Handle 403 (Forbidden) errors
    if (error.response && error.response.status === 403) {
      console.error('API Error: Forbidden access (403). User does not have permission for this resource.');
    }
    
    // General error logging
    if (error.response) {
      console.error('API Error Response:', error.response.data);
    } else if (error.request) {
      console.error('API Error Request:', error.request);
    } else {
      console.error('API Error Message:', error.message);
    }
    
    return Promise.reject(error);
  }
);

export default api;

// Example of how api.ts might be used:
// import api from './api';
// interface MyData { id: number; name: string; }
// export const fetchMyData = (): Promise<AxiosResponse<MyData>> => api.get<MyData>('/my-data-endpoint/');
// export const updateMyData = (data: MyData): Promise<AxiosResponse<MyData>> => api.post<MyData>('/update-data-endpoint/', data);
